/** Fetches the bytes of a URL with whatever credentials the app has (the default sends the MSAL bearer). */
export type BlobFetcher = (url: string) => Promise<Blob>;

/** `URL.createObjectURL` / `URL.revokeObjectURL`, injectable for tests. */
export interface ObjectUrlApi {
    create(blob: Blob): string;
    revoke(objectUrl: string): void;
}

const browserObjectUrls: ObjectUrlApi = {
    create: (blob) => URL.createObjectURL(blob),
    revoke: (objectUrl) => URL.revokeObjectURL(objectUrl),
};

interface Entry {
    promise: Promise<string>;
    objectUrl?: string;
    refs: number;
}

/**
 * Object URLs for files that need a token (OrbitalPlan/18 phase 0). One fetch per URL however many images show it:
 * `acquire` returns the same promise while the file is in use or loading, `release` marks it idle. Idle entries are kept
 * (most recently released last) so going back to a page does not refetch, and the oldest idle ones beyond `maxIdle` are
 * revoked. A failed fetch is forgotten, so the next `acquire` retries.
 */
export class AuthenticatedFileCache {
    private readonly entries = new Map<string, Entry>();

    constructor(
        private readonly fetcher: BlobFetcher,
        private readonly maxIdle: number = 100,
        private readonly objectUrls: ObjectUrlApi = browserObjectUrls
    ) {}

    acquire(url: string): Promise<string> {
        let entry = this.entries.get(url);
        if (!entry) {
            const created: Entry = { refs: 0, promise: Promise.resolve("") };
            created.promise = this.fetcher(url).then(
                (blob) => {
                    const objectUrl = this.objectUrls.create(blob);
                    if (this.entries.get(url) !== created) {
                        // cleared while loading: nobody can release it any more
                        this.objectUrls.revoke(objectUrl);
                        throw new Error("The file cache was cleared while the file was loading.");
                    }
                    created.objectUrl = objectUrl;
                    this.evictIdle();
                    return objectUrl;
                },
                (error) => {
                    if (this.entries.get(url) === created) this.entries.delete(url);
                    throw error;
                }
            );
            this.entries.set(url, created);
            entry = created;
        }
        entry.refs++;
        return entry.promise;
    }

    release(url: string): void {
        const entry = this.entries.get(url);
        if (!entry || entry.refs === 0) return;
        entry.refs--;
        if (entry.refs === 0) {
            // most recently used last: the eviction walks the map from its oldest end
            this.entries.delete(url);
            this.entries.set(url, entry);
            this.evictIdle();
        }
    }

    /** Revokes every object URL (sign-out, tests). */
    clear(): void {
        this.entries.forEach((entry) => {
            if (entry.objectUrl) this.objectUrls.revoke(entry.objectUrl);
        });
        this.entries.clear();
    }

    /** How many URLs are cached (loading, in use or idle). */
    get size(): number {
        return this.entries.size;
    }

    private evictIdle(): void {
        let idle = 0;
        this.entries.forEach((entry) => {
            if (entry.refs === 0 && entry.objectUrl) idle++;
        });
        if (idle <= this.maxIdle) return;
        const evictable: string[] = [];
        this.entries.forEach((entry, url) => {
            if (entry.refs === 0 && entry.objectUrl && idle - evictable.length > this.maxIdle) evictable.push(url);
        });
        for (const url of evictable) {
            const entry = this.entries.get(url);
            if (entry?.objectUrl) this.objectUrls.revoke(entry.objectUrl);
            this.entries.delete(url);
        }
    }
}
